import React, { useState } from 'react';
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const NONE = '__none';
const err = (e) => toast({ title: 'Error', description: e?.response?.data?.detail || 'Request failed', variant: 'destructive' });

const CrudList = ({ items, fields, endpoint, canEdit, onChanged, label, render, testPrefix }) => {
  const blank = Object.fromEntries(fields.map((f) => [f.key, f.def ?? '']));
  const [draft, setDraft] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});

  const create = async () => {
    try { await api.post(endpoint, draft); setDraft(blank); onChanged(); toast({ title: `${label} added` }); } catch (e) { err(e); }
  };
  const saveEdit = async () => {
    try { await api.put(`${endpoint}/${editId}`, edit); setEditId(null); onChanged(); toast({ title: `${label} updated` }); } catch (e) { err(e); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name || it.code}"?`)) return;
    try { await api.delete(`${endpoint}/${it.id}`); onChanged(); toast({ title: `${label} deleted` }); } catch (e) { err(e); }
  };
  const inputs = (state, setState) => fields.map((f) => (f.render ? <React.Fragment key={f.key}>{f.render(state[f.key], (v) => setState({ ...state, [f.key]: v }))}</React.Fragment> : (
    <Input key={f.key} className={`h-9 ${f.cls || ''}`} type={f.type || 'text'} placeholder={f.label} value={state[f.key]}
      onChange={(e) => setState({ ...state, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value })} data-testid={`${testPrefix}-${f.key}`} />
  )));

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex gap-2 items-center p-2 bg-gray-50 rounded-lg border">
          {inputs(draft, setDraft)}
          <Button size="sm" onClick={create} disabled={!fields.filter((f) => f.required).every((f) => String(draft[f.key]).trim())} data-testid={`${testPrefix}-add`}><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
        </div>
      )}
      <div className="divide-y border rounded-lg max-h-80 overflow-y-auto">
        {items.length === 0 && <p className="text-sm text-gray-500 p-3">Nothing saved yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`${testPrefix}-row-${it.id}`}>
            {editId === it.id ? (
              <>
                {inputs(edit, setEdit)}
                <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={saveEdit} data-testid={`${testPrefix}-save-${it.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
              </>
            ) : (
              <>
                <div className="flex-1">{render(it)}</div>
                {canEdit && (
                  <>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditId(it.id); setEdit(Object.fromEntries(fields.map((f) => [f.key, it[f.key] ?? f.def ?? '']))); }} data-testid={`${testPrefix}-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(it)} data-testid={`${testPrefix}-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const VisaCostManageDialog = ({ open, onClose, meta, onChanged }) => {
  const centreName = (id) => meta.cost_centres.find((c) => c.id === id);
  const ccSelect = (value, set) => (
    <Select value={value || NONE} onValueChange={(v) => set(v === NONE ? '' : v)}>
      <SelectTrigger className="h-9 w-48" data-testid="vc-desc-cost_centre_id"><SelectValue placeholder="Default cost centre" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No default centre</SelectItem>
        {meta.cost_centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="visa-cost-manage-dialog">
        <DialogHeader><DialogTitle>Cost descriptions & cost centres</DialogTitle></DialogHeader>
        <Tabs defaultValue="desc">
          <TabsList>
            <TabsTrigger value="desc" data-testid="vc-manage-desc-tab">Cost descriptions ({meta.descriptions.length})</TabsTrigger>
            <TabsTrigger value="cc" data-testid="vc-manage-cc-tab">Cost centres ({meta.cost_centres.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="desc">
            <CrudList items={meta.descriptions} endpoint="/api/visa-costs/descriptions" canEdit={meta.can_edit} onChanged={onChanged} label="Description" testPrefix="vc-desc"
              fields={[{ key: 'name', label: 'Description (e.g. Entry permit)', required: true, cls: 'flex-1' }, { key: 'default_amount', label: 'Default AED', type: 'number', def: 0, cls: 'w-28' }, { key: 'cost_centre_id', render: ccSelect }]}
              render={(d) => <span><b>{d.name}</b> <span className="text-gray-500">· default AED {Number(d.default_amount || 0).toFixed(2)}{centreName(d.cost_centre_id) ? ` · ${centreName(d.cost_centre_id).code}` : ''}</span></span>} />
          </TabsContent>
          <TabsContent value="cc">
            <CrudList items={meta.cost_centres} endpoint="/api/visa-costs/cost-centres" canEdit={meta.can_edit} onChanged={onChanged} label="Cost centre" testPrefix="vc-cc"
              fields={[{ key: 'code', label: 'Code (e.g. KITCHEN)', required: true, cls: 'w-36' }, { key: 'name', label: 'Name', required: true, cls: 'flex-1' }, { key: 'description', label: 'Notes', cls: 'flex-1' }]}
              render={(c) => <span><b>{c.code}</b> — {c.name}{c.description ? <span className="text-gray-500"> · {c.description}</span> : null}</span>} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
